import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, StorageUtil } from '../store';
import { Observer } from 'rxjs/Observer';
import { HttpService } from './http.service';

interface UploadFileResult {
  result: any;
  tmpDirectoryName: string;
}

@Injectable()
export class FileUploaderService {
  private url = '/api/video';
  private chunkSize = 10485760; // 10MB

  constructor(private httpService: HttpService) { }

  private static getErrorResult(reason: any): UploadFileResult {
    return {
      result: {
        succeeded: false,
        errorMessage: reason
      },
      tmpDirectoryName: ''
    };
  }

  public upload(file: File, data): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const reader = new FileReader();
      const fileName = file.name;
      reader.onload = (e: ProgressEvent) =>
        this.sendBufferData(reader.result, fileName, observer, data);
      reader.readAsArrayBuffer(file);
    });
  }

  private async sendBufferData(readResult: string | ArrayBuffer,     fileName: string, observer: Observer<boolean>, data) {
    if (readResult == null) {
      observer.error('failed reading file data');
      return;
    }
    // create directory for saving chunks by server side application.
    await this.startSendingData(fileName)
      .then(result => this.sendChunks(
        new Uint8Array(readResult as ArrayBuffer),
        result.tmpDirectoryName
      ))
      .then(result => this.endSendingData(fileName, result.tmpDirectoryName, data))
      .then(() => {
        observer.next(true);
        observer.complete();
      })
      .catch(reason => observer.error(reason));
  }

  private startSendingData(fileName: string): Promise<UploadFileResult> {
    return new Promise<UploadFileResult>(((resolve, reject) => {
      const formData = new FormData();
      formData.append('fileName', fileName);
      this.httpService.postRaw(
        `${this.url}/upload/start`,
        formData, {})
        .toPromise()
        .then(result => {
          if (result.result.succeeded) {
            resolve(result);
          } else {
            reject(result);
          }
        })
        .catch(reason => reject(FileUploaderService.getErrorResult(reason)));
    }));
  }

  private async sendChunks(buffer: Uint8Array,
    tmpDirectoryName: string): Promise<UploadFileResult> {
    return new Promise<UploadFileResult>(async (resolve, reject) => {
      let fileIndex = 0;
      const sendChunkPromises = new Array<Promise<any>>();
      for (let i = 0; i < buffer.length; i += this.chunkSize) {
        let indexTo = i + this.chunkSize;
        if (indexTo >= buffer.length) {
          indexTo = buffer.length - 1; // for last data.
        }
        const formData = new FormData();
        formData.append('file', new Blob([buffer.subarray(i, indexTo)]));
        const promise = this.httpService.postRaw(
          `${this.url}/upload/chunk`,
          formData,
          {
            headers: {
              tmpDirectory: tmpDirectoryName,
              index: fileIndex.toString()
            }
          })
          .toPromise();
        sendChunkPromises.push(promise);
        fileIndex += 1;
      }
      await Promise.all(sendChunkPromises)
        .then(results => {
          if (results.some(r => r.succeeded === false)) {
            reject('failed uploading');
          } else {
            resolve({
              result: {
                succeeded: true,
                errorMessage: ''
              },
              tmpDirectoryName
            });
          }
        })
        .catch(reason => reject(FileUploaderService.getErrorResult(reason)));
    });
  }

  private endSendingData(fileName: string, tmpDirectoryName: string, data): Promise<any> {
    return new Promise<any>(((resolve, reject) => {
      this.httpService.postRaw(
        `${this.url}/upload/end/${StorageUtil.clubID}`,
        {
          ...data,
          fileName,
          tmpDirectoryName
        },
        {
          params: {
            uploadedBy: StorageUtil.userID,
          }
        })
        .toPromise()
        .then(() => resolve())
        .catch(reason => reject(FileUploaderService.getErrorResult(reason).result));
    }));
  }

}



// WEBPACK FOOTER //
// ./src/app/services/file-uploader.service.ts